import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Zap, Save, Send } from 'lucide-react';
import {
  useCreateQuarterlyStats,
  useGenerateQuarterlyStats,
  useSubmitQuarterlyStats,
  useUpdateQuarterlyStats,
} from '@/hooks/use-h3comply';
import { useOrgToday } from '@/hooks/use-settings';
import type { QuarterlyStatistics } from '@/lib/types';

export function CreateQuarterlyStatisticsPage() {
  const orgToday = useOrgToday();
  const navigate = useNavigate();
  const createMutation = useCreateQuarterlyStats();
  const generateMutation = useGenerateQuarterlyStats();
  const updateMutation = useUpdateQuarterlyStats();
  const submitMutation = useSubmitQuarterlyStats();

  const currentYear = Number(orgToday.slice(0, 4));
  const currentQuarter = Math.ceil(Number(orgToday.slice(5, 7)) / 3);

  const [period, setPeriod] = useState<{
    year: number;
    quarter: number;
  } | null>(null);
  const year = period?.year ?? currentYear;
  const quarter = period?.quarter ?? currentQuarter;
  const [generated, setGenerated] = useState<QuarterlyStatistics | null>(null);
  const [formData, setFormData] = useState({
    total_hours_worked: '',
    total_rope_access_hours: '',
    total_technicians: 0,
    total_projects: 0,
    fatalities: 0,
    lost_time_injuries: 0,
    restricted_work_injuries: 0,
    medical_treatment_injuries: 0,
    first_aid_cases: 0,
    near_misses_reported: 0,
    incidents_reported: 0,
    toolbox_talks_conducted: 0,
    equipment_failures: 0,
    irata_reference: '',
    notes: '',
  });

  const handleAutoGenerate = async () => {
    const previousPeriod = period;
    const requestedPeriod = { year, quarter };
    setPeriod(requestedPeriod);
    let result: QuarterlyStatistics;
    try {
      result = await generateMutation.mutateAsync(requestedPeriod);
    } catch {
      setPeriod(previousPeriod);
      return;
    }
    setGenerated(result);
    setFormData({
      total_hours_worked: result.total_hours_worked,
      total_rope_access_hours: result.total_rope_access_hours,
      total_technicians: result.total_technicians,
      total_projects: result.total_projects,
      fatalities: result.fatalities,
      lost_time_injuries: result.lost_time_injuries,
      restricted_work_injuries: result.restricted_work_injuries,
      medical_treatment_injuries: result.medical_treatment_injuries,
      first_aid_cases: result.first_aid_cases,
      near_misses_reported: result.near_misses_reported,
      incidents_reported: result.incidents_reported,
      toolbox_talks_conducted: result.toolbox_talks_conducted,
      equipment_failures: result.equipment_failures,
      irata_reference: result.irata_reference,
      notes: result.notes,
    });
  };

  const handleSaveDraft = async () => {
    if (generated) {
      // Save user edits to the auto-generated record before navigating
      await updateMutation.mutateAsync({ id: generated.id, data: formData });
      navigate(`/compliance/statistics/${generated.id}`);
    } else {
      const result = await createMutation.mutateAsync({
        year,
        quarter,
        ...formData,
      });
      navigate(`/compliance/statistics/${result.id}`);
    }
  };

  const handleSubmit = async () => {
    let statsId: number;
    if (generated) {
      // Save user edits before submitting
      await updateMutation.mutateAsync({ id: generated.id, data: formData });
      statsId = generated.id;
    } else {
      const result = await createMutation.mutateAsync({
        year,
        quarter,
        ...formData,
      });
      statsId = result.id;
    }
    await submitMutation.mutateAsync(statsId);
    navigate(`/compliance/statistics/${statsId}`);
  };

  const updateField = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/compliance/statistics')}
          className="rounded-md p-1 hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Quarterly Statistics</h1>
          <p className="mt-1 text-sm text-gray-500">
            Generate from system data or enter manually
          </p>
        </div>
      </div>

      {/* Period Selection + Auto-Generate */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900">Period</h2>
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Year</label>
            <select
              value={year}
              disabled={generateMutation.isPending}
              onChange={(e) =>
                setPeriod((current) => ({
                  year: Number(e.target.value),
                  quarter: current?.quarter ?? quarter,
                }))
              }
              className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Quarter</label>
            <select
              value={quarter}
              disabled={generateMutation.isPending}
              onChange={(e) =>
                setPeriod((current) => ({
                  year: current?.year ?? year,
                  quarter: Number(e.target.value),
                }))
              }
              className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              {[1, 2, 3, 4].map((q) => (
                <option key={q} value={q}>
                  Q{q}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleAutoGenerate}
            disabled={generateMutation.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
          >
            <Zap className="h-4 w-4" />
            {generateMutation.isPending ? 'Generating...' : 'Auto-Generate from System Data'}
          </button>
        </div>
        {generated && (
          <p className="mt-3 text-sm text-green-600">
            Data auto-generated from H3Safe and H3Crew. You can override any field below.
          </p>
        )}
      </div>

      {/* Work Volume */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900">Work Volume</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Total Hours Worked
            </label>
            <input
              type="number"
              value={formData.total_hours_worked}
              onChange={(e) => updateField('total_hours_worked', e.target.value)}
              placeholder="0"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">Total man-hours across all work activities</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Total Rope Access Hours
            </label>
            <input
              type="number"
              value={formData.total_rope_access_hours}
              onChange={(e) => updateField('total_rope_access_hours', e.target.value)}
              placeholder="0"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Hours spent specifically on rope access work, separate from total hours worked.
              Used as the LTIFR denominator per IRATA guidelines.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Total Technicians</label>
            <input
              type="number"
              value={formData.total_technicians}
              onChange={(e) => updateField('total_technicians', Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Total Projects</label>
            <input
              type="number"
              value={formData.total_projects}
              onChange={(e) => updateField('total_projects', Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Injury & Incident Data */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900">Injury & Incident Data</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { key: 'fatalities', label: 'Fatalities' },
            { key: 'lost_time_injuries', label: 'Lost Time Injuries' },
            { key: 'restricted_work_injuries', label: 'Restricted Work Injuries' },
            { key: 'medical_treatment_injuries', label: 'Medical Treatment Injuries' },
            { key: 'first_aid_cases', label: 'First Aid Cases' },
            { key: 'near_misses_reported', label: 'Near Misses Reported' },
            { key: 'incidents_reported', label: 'Incidents Reported' },
            { key: 'toolbox_talks_conducted', label: 'Toolbox Talks Conducted' },
            { key: 'equipment_failures', label: 'Equipment Failures' },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700">{label}</label>
              <input
                type="number"
                value={formData[key as keyof typeof formData]}
                onChange={(e) => updateField(key, Number(e.target.value))}
                min={0}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Additional Info */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900">Additional Information</h2>
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              IRATA Reference (optional)
            </label>
            <input
              type="text"
              value={formData.irata_reference}
              onChange={(e) => updateField('irata_reference', e.target.value)}
              placeholder="IRATA confirmation reference"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Notes (optional)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3">
        <button
          onClick={() => navigate('/compliance/statistics')}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSaveDraft}
          disabled={createMutation.isPending || updateMutation.isPending}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          Save as Draft
        </button>
        <button
          onClick={handleSubmit}
          disabled={createMutation.isPending || updateMutation.isPending || submitMutation.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          Submit
        </button>
      </div>
    </div>
  );
}
